import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d76vfpgrnr7vi0ujqh4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-orange-400 text-sm font-bold tracking-widest uppercase">Get In Touch</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6">
              Ready to Start<br />
              <span className="text-orange-400">Your Project?</span>
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-10">
              Contact Allen Construction Group today for a free consultation. We serve the entire Dallas-Fort Worth area and are ready to bring your vision to life.
            </p>

            {/* Contact Details */}
            <div className="space-y-5">
              <a
                href="tel:6824502501"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-xl flex-shrink-0">
                  <i className="ri-phone-fill text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-400 text-xs uppercase tracking-widest">Call Us</div>
                  <div className="text-white font-bold text-lg group-hover:text-orange-400 transition-colors">(682) 450-2501</div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-line text-orange-400 text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-400 text-xs uppercase tracking-widest">Service Area</div>
                  <div className="text-white font-bold text-base">Dallas-Fort Worth Metroplex</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-500/20 rounded-xl flex-shrink-0">
                  <i className="ri-time-line text-orange-400 text-xl"></i>
                </div>
                <div>
                  <div className="text-gray-400 text-xs uppercase tracking-widest">Business Hours</div>
                  <div className="text-white font-bold text-base">Mon–Fri: 7AM – 6PM</div>
                  <div className="text-gray-400 text-sm">Sat: 8AM – 2PM</div>
                </div>
              </div>
            </div>

            {/* Services Quick List */}
            <div className="mt-10 p-6 bg-white/5 rounded-2xl border border-white/10">
              <div className="text-white font-bold mb-4 text-sm uppercase tracking-widest">We Specialize In</div>
              <div className="grid grid-cols-2 gap-2">
                {['New Construction', 'Structural Framing', 'Concrete Foundations', 'Flatwork & Slabs', 'Full Remodeling', 'Room Additions'].map((s) => (
                  <div key={s} className="flex items-center gap-2 text-gray-300 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-orange-400"></i>
                    </div>
                    {s}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-full">
                  <i className="ri-check-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-black text-gray-900">Message Sent!</h3>
                <p className="text-gray-500 text-base">Thanks for reaching out! We&apos;ll get back to you within 24 hours.</p>
                <a
                  href="tel:6824502501"
                  className="mt-2 bg-orange-500 text-white font-bold px-6 py-3 rounded-xl cursor-pointer whitespace-nowrap"
                >
                  Or Call (682) 450-2501
                </a>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-black text-gray-900 mb-2">Get a Free Quote</h3>
                <p className="text-gray-500 text-sm mb-6">Fill out the form and we&apos;ll reach out within 24 hours.</p>

                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="John"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Smith"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(682) 000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Service Needed *</label>
                    <select
                      name="service"
                      required
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="New Construction Framing">New Construction Framing</option>
                      <option value="Concrete Foundation">Concrete Foundation</option>
                      <option value="Concrete Flatwork">Concrete Flatwork</option>
                      <option value="Remodeling">Remodeling</option>
                      <option value="Room Addition">Room Addition</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Project Details
                      <span className="text-gray-400 font-normal ml-1">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="Tell us about your project, location, and timeline..."
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-black py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
                  >
                    {loading ? 'Sending...' : 'Send My Request'}
                  </button>

                  <p className="text-center text-gray-400 text-xs">
                    Or call us directly at{' '}
                    <a href="tel:6824502501" className="text-orange-500 font-bold cursor-pointer">
                      (682) 450-2501
                    </a>
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
